import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { BookOpen, PlayCircle, Trophy, CheckCircle, ExternalLink } from 'lucide-react';
import {
  OT_TRACKER_BOOKS,
  NT_TRACKER_BOOKS,
  TOTAL_TRACKER_BOOKS,
  bibleGatewayUrl,
} from '../data/bibleBookVideos';

/**
 * Bible Reading Tracker — grid of Bible books with two checkboxes each
 * (📺 Watched the overview video, 📖 Read the book). Progress persists per-user
 * via /api/bible-tracker. Fully completing all 66 books unlocks a large badge.
 */
const BibleReadingTracker = () => {
  const { API } = useContext(AuthContext);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // key -> true while a toggle is in flight

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bible-tracker`);
      setProgress(res.data.progress || {});
    } catch {
      toast.error('Could not load your Bible reading progress.');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const isDone = (key) => {
    const p = progress[key] || {};
    return p.watched && p.read;
  };

  const toggle = async (bookKey, field) => {
    const current = (progress[bookKey] || {})[field] || false;
    const next = !current;
    // Optimistic update
    setProgress((prev) => ({
      ...prev,
      [bookKey]: { ...(prev[bookKey] || {}), [field]: next },
    }));
    setSaving((s) => ({ ...s, [`${bookKey}:${field}`]: true }));
    try {
      const res = await axios.post(`${API}/bible-tracker/toggle`, {
        book_key: bookKey,
        field,
        value: next,
      });
      setProgress(res.data.progress || {});
    } catch {
      // Roll back on failure
      setProgress((prev) => ({
        ...prev,
        [bookKey]: { ...(prev[bookKey] || {}), [field]: current },
      }));
      toast.error('Could not save that update. Try again.');
    } finally {
      setSaving((s) => {
        const copy = { ...s };
        delete copy[`${bookKey}:${field}`];
        return copy;
      });
    }
  };

  const completedCount = [...OT_TRACKER_BOOKS, ...NT_TRACKER_BOOKS].filter((b) => isDone(b.key)).length;
  const pct = Math.round((completedCount / TOTAL_TRACKER_BOOKS) * 100);
  const allComplete = completedCount === TOTAL_TRACKER_BOOKS;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500">Loading your Bible tour progress…</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="bible-reading-tracker">
      {/* Progress summary */}
      <Card
        className={
          allComplete
            ? 'border-2 border-amber-400 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50'
            : 'border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50'
        }
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className={allComplete ? 'text-amber-800 flex items-center gap-2' : 'text-[#2B6B7F] flex items-center gap-2'}>
                {allComplete ? <Trophy className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
                {allComplete ? 'You’ve Toured the Whole Bible!' : 'Bible Tour Progress'}
              </CardTitle>
              <CardDescription>
                {allComplete
                  ? 'Every book — watched and read. That is a lifetime achievement of the highest order.'
                  : `Tick “📺 Watched” once you finish that book’s BibleProject overview and “📖 Read” once you’ve read the book itself.`}
              </CardDescription>
            </div>
            <div className="text-right">
              <p className="text-3xl font-heading font-bold text-[#2B6B7F]">{completedCount}<span className="text-lg text-slate-400">/{TOTAL_TRACKER_BOOKS}</span></p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">books complete</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={pct} className="h-3" />
          <p className="text-xs text-slate-500 mt-2">{pct}% of the whole Bible</p>
          {allComplete && (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-lg bg-white border border-amber-300" data-testid="bible-tour-badge">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">Whole-Bible Tour Champion</p>
                <p className="text-xs text-amber-800">Watched the overview and read every book — 39 OT + 27 NT.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Old Testament */}
      <Card>
        <CardHeader>
          <CardTitle className="text-purple-700 flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Old Testament
            <span className="ml-auto text-xs font-normal text-slate-500">
              {OT_TRACKER_BOOKS.filter((b) => isDone(b.key)).length}/{OT_TRACKER_BOOKS.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BookGrid books={OT_TRACKER_BOOKS} progress={progress} saving={saving} onToggle={toggle} accent="purple" />
        </CardContent>
      </Card>

      {/* New Testament */}
      <Card>
        <CardHeader>
          <CardTitle className="text-indigo-700 flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> New Testament
            <span className="ml-auto text-xs font-normal text-slate-500">
              {NT_TRACKER_BOOKS.filter((b) => isDone(b.key)).length}/{NT_TRACKER_BOOKS.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BookGrid books={NT_TRACKER_BOOKS} progress={progress} saving={saving} onToggle={toggle} accent="indigo" />
        </CardContent>
      </Card>
    </div>
  );
};

// Grid of book rows — each row has the name, two toggle chips, and a Read-on-Bible-Gateway link.
const BookGrid = ({ books, progress, saving, onToggle, accent }) => {
  const activeClass = accent === 'purple'
    ? 'bg-purple-600 text-white border-purple-600'
    : 'bg-indigo-600 text-white border-indigo-600';

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`book-grid-${accent}`}>
      {books.map((b) => {
        const p = progress[b.key] || {};
        const done = p.watched && p.read;
        return (
          <div
            key={b.key}
            className={`flex items-center gap-2 p-2 rounded-lg border ${done ? 'bg-green-50 border-green-300' : 'bg-white border-slate-200'}`}
            data-testid={`book-row-${b.key}`}
          >
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {done && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
              <p className={`text-sm font-medium truncate ${done ? 'text-green-900' : 'text-slate-800'}`}>{b.name}</p>
            </div>
            <button
              type="button"
              onClick={() => onToggle(b.key, 'watched')}
              disabled={!!saving[`${b.key}:watched`]}
              className={`text-xs px-2 py-1 rounded-md border transition-colors flex items-center gap-1 ${p.watched ? activeClass : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
              title="Watched the video"
              data-testid={`toggle-watched-${b.key}`}
            >
              <PlayCircle className="w-3 h-3" />
              Watched
            </button>
            <button
              type="button"
              onClick={() => onToggle(b.key, 'read')}
              disabled={!!saving[`${b.key}:read`]}
              className={`text-xs px-2 py-1 rounded-md border transition-colors flex items-center gap-1 ${p.read ? activeClass : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
              title="Read the book"
              data-testid={`toggle-read-${b.key}`}
            >
              <BookOpen className="w-3 h-3" />
              Read
            </button>
            <a
              href={bibleGatewayUrl(b.name)}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${b.name} on Bible Gateway`}
              className="text-slate-400 hover:text-teal-700 flex-shrink-0"
              data-testid={`gateway-${b.key}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        );
      })}
    </div>
  );
};

export default BibleReadingTracker;
